/**
 * KaiWidget — the standalone "Ask Kai" assistant.
 *
 * A floating button (bottom-right, just left of the Support FAB) that
 * opens a chat panel. It talks to kai-service (`/kai/ask`), which answers
 * only from the platform knowledge base with guardrails — never prices,
 * addresses, or PII. The thread is kept in component state via the
 * `conversation_id` echoed back on each answer.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Input, Spinner } from 'reactstrap';
import { FiX, FiSend } from 'react-icons/fi';
import kaiService from '../../services/kaiService';
import { useTranslation } from '../../contexts/LanguageContext';

const ACCENT = '#7c3aed';

// Quick-start chips mirror KAI's five "powers" from the mascot brand —
// Find Warehouses · Compare Spaces · Best Deals · Smart Logistics ·
// Unlock Value — phrased as questions KAI can answer from the knowledge base.
const SUGGESTIONS = [
  'How do I find warehouses near me?',
  'How do I compare warehouses?',
  'How is warehouse pricing decided?',
  'Can you help with logistics and transport?',
  'How do I list my warehouse and earn?',
];

const KaiWidget = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role:'USER'|'KAI', text, sources?}
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const convoRef = useRef(null);
  const scrollRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        !e.target.closest('.kai-widget-trigger')
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const send = async (q) => {
    const question = (q ?? text).trim();
    if (!question || sending) return;
    setMessages((prev) => [...prev, { role: 'USER', text: question }]);
    setText('');
    setSending(true);
    try {
      const { data } = await kaiService.ask(question, convoRef.current);
      convoRef.current = data.conversation_id;
      setMessages((prev) => [
        ...prev,
        { role: 'KAI', text: data.answer, sources: data.sources || [] },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'KAI',
          text: t(
            'kai.error',
            "Sorry, I couldn't reach the assistant just now. Please try again.",
          ),
          sources: [],
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <>
      {/* Floating trigger — sits just left of the Support FAB */}
      <button
        className="kai-widget-trigger"
        onClick={() => setIsOpen((o) => !o)}
        title={t('kai.title', 'Ask KAI')}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 92,
          zIndex: 9999,
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: 'none',
          background: `linear-gradient(135deg, ${ACCENT}, #a855f7)`,
          color: '#fff',
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(124,58,237,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 15,
          letterSpacing: 0.2,
        }}
      >
        {isOpen ? <FiX size={22} /> : (
          <img
            src="/kai-avatar-v2.png"
            alt="KAI"
            style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%' }}
          />
        )}
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            bottom: 90,
            right: 24,
            zIndex: 9998,
            width: 370,
            maxHeight: 560,
            height: 560,
            borderRadius: 14,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            background: '#fff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            animation: 'kaiSlideUp 0.22s ease-out',
          }}
        >
          {/* Header */}
          <div
            style={{
              background: `linear-gradient(135deg, ${ACCENT}, #a855f7)`,
              color: '#fff',
              padding: '14px 18px',
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center" style={{ gap: 10 }}>
                <img
                  src="/kai-avatar-v2.png"
                  alt="KAI"
                  style={{
                    width: 40, height: 40, borderRadius: '50%', objectFit: 'contain',
                    background: 'rgba(255,255,255,0.12)',
                    border: '2px solid rgba(255,255,255,0.65)', flexShrink: 0,
                  }}
                />
                <div>
                  <div className="fw-bold" style={{ fontSize: 15 }}>
                    {t('kai.title', 'Ask KAI')}
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.85 }}>
                    {t('kai.tagline', 'Key AI · your key to the right warehouse')}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}
                aria-label="Close"
              >
                <FiX size={20} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f8fafc' }}>
            {messages.length === 0 ? (
              <div>
                <div className="text-muted" style={{ fontSize: 13, marginBottom: 12 }}>
                  {t(
                    'kai.greeting',
                    "Hi! I'm KAI — Key AI, your key to unlocking the right warehouse. 🔑 I can help you find and compare spaces, understand pricing, KYC, contracts, and logistics, or list your own warehouse. I won't share prices or private details. How can I help you today?",
                  )}
                </div>
                <div className="d-flex flex-column gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="text-start"
                      style={{
                        padding: '9px 12px',
                        borderRadius: 10,
                        border: '1px solid #e2e8f0',
                        background: '#fff',
                        cursor: 'pointer',
                        fontSize: 13,
                        color: '#334155',
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => {
                const mine = m.role === 'USER';
                return (
                  <div key={i} className="d-flex mb-2" style={{ justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                    <div
                      style={{
                        maxWidth: '82%',
                        padding: '9px 12px',
                        borderRadius: 13,
                        background: mine ? ACCENT : '#fff',
                        color: mine ? '#fff' : '#0f172a',
                        border: mine ? 'none' : '1px solid #e2e8f0',
                        boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
                        fontSize: 13.5,
                      }}
                    >
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</div>
                      {!mine && m.sources && m.sources.length > 0 && (
                        <div style={{ marginTop: 6, fontSize: 10.5, color: '#64748b' }}>
                          {t('kai.source', 'Source')}: {m.sources.map((s) => s.title).join(' · ')}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            {sending && (
              <div className="d-flex align-items-center text-muted" style={{ fontSize: 12, gap: 6 }}>
                <Spinner size="sm" /> {t('kai.thinking', 'KAI is thinking…')}
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="d-flex gap-2" style={{ padding: 12, borderTop: '1px solid #e2e8f0' }}>
            <Input
              type="textarea"
              rows={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('kai.placeholder', 'Ask Kai a question…')}
              style={{ resize: 'none', borderRadius: 10, fontSize: 13.5 }}
              maxLength={1000}
            />
            <button
              onClick={() => send()}
              disabled={sending || !text.trim()}
              style={{
                minWidth: 44,
                borderRadius: 10,
                border: 'none',
                background: ACCENT,
                color: '#fff',
                cursor: sending || !text.trim() ? 'default' : 'pointer',
                opacity: sending || !text.trim() ? 0.5 : 1,
              }}
              aria-label="Send"
            >
              <FiSend size={16} />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes kaiSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default KaiWidget;
